import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Textarea } from "@/components/ui/textarea";
import { Edit, X, Save } from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { updateTag, deleteTag } from "@/lib/stores/tagsStore";
import { TagItem } from "@/lib/stores/tagsStore";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TagBadgeProps {
  tag: TagItem;
  onRefresh: () => void;
}

interface EditingTag {
  name: string;
  group: string;
  description: string;
  color: string;
}

export default function TagBadge({ tag, onRefresh }: TagBadgeProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();

  // State for editing this specific tag
  const [isEditing, setIsEditing] = useState(false);
  const [editingTag, setEditingTag] = useState<EditingTag>({
    name: tag.name,
    group: tag.group_name || "",
    description: tag.description || "",
    color: tag.color || "#6366f1"
  });

  // Color options for editing tags
  const tagColors = [
    "#6366f1", // Indigo
    "#ef4444", // Red
    "#10b981", // Green
    "#f59e0b", // Amber
    "#8b5cf6", // Violet
    "#06b6d4", // Cyan
    "#f97316", // Orange
    "#ec4899", // Pink
    "#84cc16", // Lime
    "#6b7280", // Gray
  ];

  // Handle tag operations
  const handleUpdateTag = async () => {
    if (!selectedGame || !editingTag.name.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name for the tag",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateTag({
        id: tag.id,
        game_id: selectedGame.id,
        name: editingTag.name.trim(),
        group: editingTag.group.trim() || undefined,
        description: editingTag.description.trim() || undefined,
        color: editingTag.color,
      }, toast);

      setIsEditing(false);
      
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
      
      onRefresh();
    } catch (error) {
      console.error("Error updating tag:", error);
      toast({
        title: "Error",
        description: "Failed to update tag. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedGame) return;

    try {
      await deleteTag(tag.id, selectedGame.id, toast);
      
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
      
      onRefresh();
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast({
        title: "Error",
        description: "Failed to delete tag. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setEditingTag({
      name: tag.name,
      group: tag.group_name || "",
      description: tag.description || "",
      color: tag.color || "#6366f1"
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingTag({
      name: tag.name,
      group: tag.group_name || "",
      description: tag.description || "",
      color: tag.color || "#6366f1"
    });
  };

  if (isEditing) {
    return (
      <div className="bg-white border border-input rounded-lg p-3 space-y-3 min-w-80">
        <div className="grid grid-cols-2 gap-3">
          <Input
            value={editingTag.name}
            onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
            placeholder={t('platform.cards.widgets.tag_name')}
            size={8}
          />
          <div>
            <div className="flex gap-1 flex-wrap">
              {tagColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-5 h-5 rounded-full border-2 ${
                    editingTag.color === color ? 'border-foreground' : 'border-input'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setEditingTag({ ...editingTag, color })}
                />
              ))}
            </div>
          </div>
        </div>
        <Input
          value={editingTag.group}
          onChange={(e) => setEditingTag({ ...editingTag, group: e.target.value })}
          placeholder={t('platform.cards.widgets.group_placeholder')}
          size={8}
        />
        <Textarea
          value={editingTag.description}
          onChange={(e) => setEditingTag({ ...editingTag, description: e.target.value })}
          placeholder={t('platform.common.description')}
          rows={2}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={handleUpdateTag}
            className="flex items-center gap-2"
          >
            <Save className="h-3 w-3" />
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelEditing}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group">
      <div
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-foreground relative"
        style={{ backgroundColor: tag.color || "#6366f1" }}
      >
        {/* Tag content */}
        <div className="w-1.5 h-1.5 rounded-full bg-white bg-opacity-40" />
        <span className="text-xs">{tag.name}</span>
        
        {/* Action buttons inside badge */}
        <div className="flex items-center gap-0.5 ml-1">
          <button
            onClick={startEditing}
            className="h-4 w-4 flex items-center justify-center rounded-full bg-black bg-opacity-20 hover:bg-opacity-30 transition-all"
            title={t('platform.cards.widgets.edit_tag')}
          >
            <Edit className="h-2.5 w-2.5" />
          </button>
          <button
            onClick={handleDeleteTag}
            className="h-4 w-4 flex items-center justify-center rounded-full bg-black bg-opacity-20 hover:bg-opacity-30 transition-all"
            title={t('platform.cards.widgets.delete_tag')}
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
        
        {/* Tooltip for description */}
        {tag.description && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-foreground text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {tag.description}
          </div>
        )}
      </div>
    </div>
  );
}
